import { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";

import { apiSlice } from "app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import { ApprovalConfirmationGrid } from "./ApprovalConfirmationGrid";
import { type ApprovalModalProps, type MilestoneApproval } from "../types";
import { formatApprovalAmount } from "../utils/formatters";

export const GroupApprovalModal = ({
  isOpen,
  onClose,
  milestones,
  groupType,
  groupName,
  parentBrand,
  parentDate,
  actionType,
}: ApprovalModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateMilestoneApproval] =
    apiSlice.useUpdateMilestoneApprovalMutation();

  const { totalAmount, validMilestones } = useMemo(() => {
    // Filter milestones based on action type and current approval status
    const filtered = milestones.filter((m: MilestoneApproval) => {
      if (!m._id) return false; // Must have valid ID

      // For "approve" action: only include unapproved milestones
      if (actionType === "approve") {
        return !m.isPaymentApproved;
      }

      // For "unapprove" action: only include approved milestones
      if (actionType === "unapprove") {
        return m.isPaymentApproved;
      }

      return false;
    });

    const total = filtered.reduce((sum, milestone) => {
      const amount = Number(milestone.holdBalance ?? milestone.rate ?? 0);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);

    return { totalAmount: total, validMilestones: filtered };
  }, [milestones, actionType]);

  const getGroupDescription = () => {
    const actionText = actionType === "approve" ? "approve" : "unapprove";

    switch (groupType) {
      case "dateGroup":
        return (
          <>
            {actionText} the following milestones for payment cycle{" "}
            <strong>{groupName}</strong>
          </>
        );
      case "brandGroup":
        return (
          <>
            {actionText} the following milestones for brand{" "}
            <strong>{groupName}</strong> for payment cycle{" "}
            <strong>{parentDate}</strong>
          </>
        );
      case "campaignGroup":
        return (
          <>
            {actionText} the following milestones for campaign{" "}
            <strong>{groupName}</strong> for brand{" "}
            <strong>{parentBrand}</strong> for payment cycle{" "}
            <strong>{parentDate}</strong>
          </>
        );
      default:
        return <>{actionText} the following milestone(s)</>;
    }
  };

  const handleConfirmAction = async () => {
    if (validMilestones.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const milestoneIds = validMilestones.map((m) => m._id);
      const isPaymentApproved = actionType === "approve";

      await updateMilestoneApproval({
        milestoneIds,
        isPaymentApproved,
      }).unwrap();

      // Success - show success toast and close modal
      const groupDescription =
        groupType === "dateGroup"
          ? `payment cycle ${groupName}`
          : groupType === "brandGroup"
            ? `brand ${groupName}`
            : `campaign ${groupName}`;

      showToast({
        type: "success",
        message: `Successfully ${actionType === "approve" ? "approved" : "unapproved"} ${validMilestones.length} milestone(s) for ${groupDescription}.`,
      });
      onClose();
    } catch (err: any) {
      setError(
        err?.data?.message ||
          err?.message ||
          `Failed to ${actionType} milestones. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isApproveAction = actionType === "approve";
  const actionLabel = isApproveAction ? "Approve" : "Unapprove";
  const buttonColor = isApproveAction ? "success" : "danger";

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Confirm Group {actionLabel}</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <strong>{actionLabel} Failed:</strong> {error}
          </Alert>
        )}

        <div className="mb-4">
          <p className="mb-2">
            Are you sure you want to {getGroupDescription()}?
          </p>
          <h5>{actionLabel} Summary</h5>
          <ul className="list-unstyled mb-0">
            <li>
              <strong>Number of milestones:</strong> {validMilestones.length}
            </li>
            <li>
              <strong>Total amount:</strong> {formatApprovalAmount(totalAmount)}
            </li>
            <li>
              <strong>Action:</strong> {actionLabel} for payment processing
            </li>
          </ul>
        </div>

        {validMilestones.length > 0 && (
          <div>
            <h6>
              Milestones to be{" "}
              {actionType === "approve" ? "approved" : "unapproved"}:
            </h6>
            <ApprovalConfirmationGrid
              milestones={validMilestones}
              height={300}
            />
          </div>
        )}

        {validMilestones.length === 0 && (
          <Alert color="warning">
            No {actionType === "approve" ? "unapproved" : "approved"} milestones
            found to {actionType}.
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color={buttonColor}
          onClick={handleConfirmAction}
          disabled={isSubmitting || validMilestones.length === 0}
        >
          {isSubmitting
            ? "Processing..."
            : `${actionLabel} ${validMilestones.length} milestone(s)`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
